/**
 * Wave 3.49 — Client-side carat/piece validation helpers.
 *
 * Mirrors the backend rules in ``backend/core/carat_validation.py`` so
 * the UI can surface inline / toast errors *before* the network round
 * trip. The backend remains the source of truth — these helpers exist
 * solely for UX polish.
 *
 * Rule 1 — both ``carats > 0`` AND ``pieces > 0`` are mandatory on every
 *          carat-material movement (issue, receive, return, transfer,
 *          recut, sale, purchase-return).
 *
 * Rule 2 — after the movement, BOTH source and destination must satisfy
 *          ``(carats > 0) IFF (pieces > 0)``. A 1-piece, 42.39ct packet
 *          cannot issue "1 pc / 4.39 ct" — that would leave 0pc/38ct
 *          which is forbidden.
 *
 * Exceptions (documented):
 *   1. Requirements on order forms — either ``pieces = 0`` OR
 *      ``carats = 0`` is allowed (aspirational quantity).
 *   2. Goldsmith → Broken loss row — ``loss_pieces`` may be 0 with
 *      ``loss_carats > 0`` (weight loss without piece loss).
 *
 * Gold is EXCLUDED (it uses grams, no pieces concept).
 */

const EPS = 1e-9;

const num = (v) => Number(v || 0);

/**
 * Rule 1 — both qty must be > 0.
 *
 * Returns ``null`` on success, or a contextual error message string.
 */
export function validateMovementQty(carats, pieces, opts = {}) {
  const {
    context = 'transaction',
    packetNo = '',
    availableCarats = null,
    availablePieces = null,
  } = opts;
  const ct = num(carats);
  const pcs = Math.trunc(num(pieces));

  if (ct < 0 || pcs < 0) {
    return `Negative values not allowed for ${context}. Received ${ct} ct / ${pcs} pcs.`;
  }
  if (ct <= EPS && pcs <= 0) {
    return `Carats and pieces are both required for ${context}. Enter a non-zero value for each.`;
  }
  const prefix = packetNo ? `Source packet ${packetNo}: ` : '';
  const availSuffix =
    (availableCarats !== null && availablePieces !== null)
      ? ` Available: ${Number(availableCarats).toFixed(3)} ct / ${availablePieces} pcs.`
      : '';
  if (ct <= EPS) {
    return `${prefix}Pieces without carats are not allowed for ${context}: you entered ${pcs} pcs with ${ct} ct.${availSuffix}`;
  }
  if (pcs <= 0) {
    return `${prefix}Carats without pieces are not allowed for ${context}: you entered ${ct} ct with ${pcs} pcs.${availSuffix}`;
  }
  return null;
}

/**
 * Rule 2 — after the movement, ``(carats > 0) IFF (pieces > 0)``.
 *
 * Returns ``null`` on success, or a contextual error message string.
 * Pass the post-move (remaining) balance for the side you want to check.
 */
export function assertStateConsistent(remainingCarats, remainingPieces, opts = {}) {
  const {
    packetNo = '',
    label = 'source packet',
    context = 'transaction',
    txnCarats = null,
    txnPieces = null,
  } = opts;
  const rct = num(remainingCarats);
  const rpcs = Math.trunc(num(remainingPieces));

  const ok = (rct > EPS && rpcs > 0) || (Math.abs(rct) <= EPS && rpcs === 0);
  if (ok) return null;

  const pkt = packetNo ? `${label} ${packetNo}` : label;
  let msg;
  if (Math.abs(rct) <= EPS && rpcs > 0) {
    msg = `${context} would leave ${pkt} with ${rpcs} piece(s) but 0 carats — pieces cannot exist without weight.`;
  } else if (rct > EPS && rpcs === 0) {
    msg = `${context} would leave ${pkt} with ${rct.toFixed(3)} ct but 0 pieces — weight cannot exist without a piece carrier.`;
  } else {
    msg = `${context} would leave ${pkt} in an inconsistent state (${rct.toFixed(3)} ct, ${rpcs} pcs).`;
  }
  if (txnCarats !== null && txnPieces !== null) {
    msg += ` You attempted to move ${num(txnCarats).toFixed(3)} ct / ${Math.trunc(num(txnPieces))} pcs. Tip: when a packet has only 1 piece you must move all carats with it; partial weight transfers are only valid when more than one piece is moving and both sides retain consistent (carats, pieces).`;
  }
  return msg;
}

/**
 * Convenience wrapper — Rule 1 on the requested qty, balance-check, and
 * Rule 2 on the source's resulting balance. Returns null on success, or
 * a single combined error string on the first failure.
 */
export function validateCaratMovement({
  txnCarats,
  txnPieces,
  availableCarats,
  availablePieces,
  packetNo = '',
  context = 'transaction',
}) {
  const r1 = validateMovementQty(txnCarats, txnPieces, {
    context,
    packetNo,
    availableCarats,
    availablePieces,
  });
  if (r1) return r1;

  const ct = num(txnCarats);
  const pcs = Math.trunc(num(txnPieces));
  const availCt = num(availableCarats);
  const availPcs = Math.trunc(num(availablePieces));

  if (ct - availCt > EPS) {
    return `Insufficient carats on packet ${packetNo}. Available: ${availCt.toFixed(3)} ct, requested: ${ct.toFixed(3)} ct.`;
  }
  if (pcs - availPcs > 0) {
    return `Insufficient pieces on packet ${packetNo}. Available: ${availPcs} pcs, requested: ${pcs} pcs.`;
  }

  return assertStateConsistent(availCt - ct, availPcs - pcs, {
    packetNo,
    label: 'source packet',
    context,
    txnCarats: ct,
    txnPieces: pcs,
  });
}

/**
 * Tiny helper that returns ``true`` when the qty pair passes Rule 1
 * (no contextual message). Useful for disabling submit buttons.
 */
export function isValidMovementQty(carats, pieces) {
  return num(carats) > EPS && Math.trunc(num(pieces)) > 0;
}
